import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

function badgeClass(kind) {
  switch (kind) {
    case "runnable":
      return "culture-readiness-badge culture-readiness-badge--runnable";
    case "blocked":
      return "culture-readiness-badge culture-readiness-badge--blocked";
    case "reference":
      return "culture-readiness-badge culture-readiness-badge--reference";
    case "moderation":
      return "culture-readiness-badge culture-readiness-badge--moderation";
    case "warning":
      return "culture-readiness-badge culture-readiness-badge--warning";
    case "info":
      return "culture-readiness-badge culture-readiness-badge--info";
    default:
      return "culture-readiness-badge";
  }
}

const READINESS_PATH_TITLES = {
  ready_for_parser: "This source has a usable feed and is ready for its parser to ingest events.",
  deferred_no_stable_feed: "Discovery did not find a stable public feed, so ingest is deferred.",
  discovery_pending: "This source is still being investigated for a usable calendar or API feed.",
  manual_curation: "This source is maintained by curated seed data, not an automated parser.",
  blocked_api_access: "Ingest is blocked until API access is approved.",
  blocked_terms: "Ingest is blocked pending a terms-of-use review.",
};

function formatPathLabel(path) {
  return String(path || "").replace(/_/g, " ");
}

export function CultureSourceChip({ label, kind = "info", title = "", className }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const tooltipId = useId();
  const tip = String(title || "").trim();

  const updatePosition = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const width = 280;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const top = rect.bottom + 8;
    setCoords({ top, left });
  };

  const show = () => {
    if (!tip) {
      return;
    }
    updatePosition();
    setOpen(true);
  };

  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onScroll = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  if (!label) {
    return null;
  }

  return (
    <>
      <span
        ref={ref}
        className={className || badgeClass(kind)}
        aria-label={tip ? `${label}. ${tip}` : label}
        aria-describedby={open && tip ? tooltipId : undefined}
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {label}
      </span>
      {open && tip && typeof document !== "undefined"
        ? createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              className="culture-source-chip-tooltip"
              style={{ top: coords.top, left: coords.left }}
            >
              <p className="culture-source-chip-tooltip__label">{label}</p>
              <p className="culture-source-chip-tooltip__body">{tip}</p>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

const CultureSourceReadinessBadge = ({ label, kind = "info", title = "" }) => (
  <CultureSourceChip label={label} kind={kind} title={title} />
);

export function resolveCultureAdminSourceType(source = {}) {
  if (source.runnerType === "virtual_regional_feed") {
    return "Virtual Feed";
  }
  if (source.readinessPath === "deferred_no_stable_feed" || source.discoveryStatus === "deferred_no_stable_feed") {
    return "Deferred";
  }
  if (source.runnerType === "culture_national_venues" || source.sourceCategory === "venue_feed") {
    return "National Venue";
  }
  if (source.runnerType === "culture_national_events") {
    return "National Event";
  }
  return source.sourceCategory || source.sourceType || "—";
}

function percentLabel(value) {
  return value != null ? `${Math.round(Number(value) * 100)}%` : "—";
}

export function resolveCultureReadinessBadges(source = {}) {
  const badges = [];

  if (source.isOfficial) {
    badges.push({
      key: "official",
      label: "Official Source",
      kind: "info",
      title: "Official municipal or institutional source, not a third-party aggregator.",
    });
  }

  if (source.readinessTier === "A") {
    badges.push({
      key: "tier-a",
      label: "Runnable",
      kind: "runnable",
      title: "Tier A — this source is production-ready and can be run or scheduled.",
    });
  } else if (
    source.readinessTier === "B" &&
    source.supportsRunNow &&
    source.adminOperable !== false
  ) {
    badges.push({
      key: "tier-b-candidate",
      label: "Production Candidate",
      kind: "warning",
      title: "Tier B source with Admin Run Now enabled. Scheduling is still pending.",
    });
  }

  if (source.runnerType === "culture_national_events") {
    badges.push({
      key: "national-event",
      label: "National Event",
      kind: "info",
      title: "National-level event ingest feed, not a local municipal calendar.",
    });
  }

  if (source.sourceCategory === "venue_feed" || source.runnerType === "culture_national_venues") {
    badges.push({
      key: "venue-feed",
      label: "Venue Feed",
      kind: "info",
      title: source.venuePipelineType
        ? `OSM regional venue feed (${source.venuePipelineType}). This loads venues, not events.`
        : "Venue pipeline source. This loads venues, not an event parser.",
    });
  } else if (
    source.readinessTier === "B" &&
    source.runnerType !== "virtual_regional_feed" &&
    !(source.supportsRunNow && source.adminOperable !== false)
  ) {
    badges.push({
      key: "tier-b",
      label: "Blocked",
      kind: "blocked",
      title:
        source.recommendedNextAction ||
        "Tier B — not runnable yet. Open Details to see blockers and the next action.",
    });
  } else if (source.runnerType === "virtual_regional_feed") {
    badges.push({
      key: "virtual-feed",
      label: source.feedStrategy === "composite_feed" ? "Composite Feed" : "Virtual Feed",
      kind: "info",
      title:
        source.virtualFeedDescription ||
        "Virtual regional feed. It aggregates child municipal feeds and does not run directly.",
    });
  } else if (source.referenceOnly) {
    badges.push({
      key: "reference",
      label: "Reference",
      kind: "reference",
      title: "Reference-only source. Use the curated seed workflow instead of Run Now.",
    });
  } else if (source.moderationBased) {
    badges.push({
      key: "moderation",
      label: "Moderation",
      kind: "moderation",
      title: "User-submission source. Review items in the Culture moderation queue.",
    });
  } else if (source.readinessTier === "C") {
    badges.push({
      key: "tier-c",
      label: "Reference",
      kind: "reference",
      title: "Tier C reference source. It is tracked for discovery, not automated ingest.",
    });
  }

  if (source.readinessPath) {
    badges.push({
      key: "readiness-path",
      label: formatPathLabel(source.readinessPath),
      kind: source.readinessPath === "ready_for_parser" ? "runnable" : "info",
      title:
        READINESS_PATH_TITLES[source.readinessPath] ||
        (source.parserCandidate
          ? `Readiness path ${formatPathLabel(source.readinessPath)}. Parser: ${source.parserCandidate}.`
          : `Readiness path ${formatPathLabel(source.readinessPath)}.`),
    });
  }

  if (source.parserCandidate && source.readinessPath === "ready_for_parser") {
    badges.push({
      key: "parser-candidate",
      label: source.parserCandidate,
      kind: "info",
      title: `Parser used to ingest this feed: ${source.parserCandidate}.`,
    });
  }

  if (source.isAggregator) {
    badges.push({
      key: "aggregator",
      label: "Aggregator",
      kind: "warning",
      title: "Third-party event aggregator — not an official municipal source.",
    });
  }

  if (source.reviewDecision === "blocked_api_access" || source.reviewDecision === "blocked_terms") {
    badges.push({
      key: "legal-blocked",
      label: "Legal/API review required",
      kind: "blocked",
      title:
        source.reviewDecision === "blocked_terms"
          ? "Ingest is blocked until terms of use are reviewed."
          : "Ingest is blocked until API access is approved.",
    });
  }

  if (source.apiKeyEnvVar && source.envVarPresent === false) {
    badges.push({
      key: "api-key-missing",
      label: "API key required",
      kind: "warning",
      title: `Set ${source.apiKeyEnvVar} before this source can run.`,
    });
  }

  if (source.regionKey) {
    badges.push({
      key: "region",
      label: formatPathLabel(source.regionKey),
      kind: "info",
      title: `Culture region this source covers: ${formatPathLabel(source.regionKey)}.`,
    });
  }

  if (source.municipality) {
    badges.push({
      key: "municipality",
      label: source.municipality,
      kind: "info",
      title: `Municipality this source is scoped to: ${source.municipality}.`,
    });
  }

  if (source.portalType && source.portalType !== "DISCOVERY_PENDING") {
    badges.push({
      key: "portal",
      label: source.portalType,
      kind: "info",
      title: `Discovered portal type: ${source.portalType}.`,
    });
  }

  if (source.parserType === "wordpress_rest_with_detail_enrichment") {
    badges.push({
      key: "wp-detail-enricher",
      label: "WordPress REST + Detail Enricher",
      kind: "info",
      title:
        "Parses WordPress REST event listings and enriches each item from its public detail page.",
    });
  }

  if (source.parserImplemented) {
    badges.push({
      key: "parser",
      label: "Parser ready",
      kind: "info",
      title: "An ingestion parser is implemented for this source.",
    });
  }

  if (source.envVarRequired && source.envVarPresent === false) {
    badges.push({
      key: "env-missing",
      label: "Env var missing",
      kind: "warning",
      title: source.envVarName
        ? `Required environment variable ${source.envVarName} is not set.`
        : "A required environment variable is not set, so this source cannot run.",
    });
  }

  if (source.liveKeyListed) {
    badges.push({
      key: "live",
      label: "Live key",
      kind: "info",
      title: "Listed as a production-live culture ingestion source.",
    });
  }

  if (source.readinessPath === "deferred_no_stable_feed" || source.discoveryStatus === "deferred_no_stable_feed") {
    badges.push({
      key: "deferred",
      label: "Deferred",
      kind: "warning",
      title: source.recommendedNextAction || "No stable feed yet, so automated ingest is deferred.",
    });
  }

  if (source.scheduleKeys?.length) {
    badges.push({
      key: "schedule-coverage",
      label: source.scheduled ? "Scheduled" : "Schedule defined",
      kind: source.scheduled ? "info" : "warning",
      title: source.scheduled
        ? `Covered by an enabled schedule group: ${source.scheduleKeys.join(", ")}.`
        : `Schedule group exists but is not enabled: ${source.scheduleKeys.join(", ")}.`,
    });
  } else if (source.scheduled) {
    badges.push({
      key: "scheduled",
      label: "Scheduled",
      kind: "info",
      title: "This source is included in an enabled culture schedule.",
    });
  }

  if (source.manualOnly) {
    badges.push({
      key: "manual-only",
      label: "Manual only",
      kind: "warning",
      title: "Enabled Tier A source that is not covered by an enabled schedule group.",
    });
  }

  if (source.adminMetrics) {
    const metrics = source.adminMetrics;
    badges.push({
      key: "quality",
      label: "Quality",
      kind: "info",
      title: [
        `Upcoming events: ${metrics.upcomingEventCount ?? "—"}.`,
        `Venue match: ${percentLabel(metrics.venueMatchRate)}.`,
        `Tag coverage: ${percentLabel(metrics.tagCoverageRate)}.`,
      ].join(" "),
    });
  }

  if (
    source.recommendedNextAction &&
    !badges.some((badge) => badge.title === source.recommendedNextAction)
  ) {
    badges.push({
      key: "next-action",
      label: "Next action",
      kind: "warning",
      title: source.recommendedNextAction,
    });
  }

  return badges;
}

export default CultureSourceReadinessBadge;
